/**
 * auoDisposition — the SINGLE source of truth for how an AUO engine disposition
 * is PRESENTED across BOTH AUO surfaces:
 *   · the inline staff panel  — AuoReviewPanel (inline-style, hex/token colours)
 *   · the canonical workspace — EvalDispositionList (CSS-class chips)
 *
 * Extracted 2026-07-01 (Task 7) so the SAME underlying per-zone ruling data
 * (`fixtures[].status` → engine `disposition`) can never again render with two
 * divergent vocabularies. The owner-confirmed canonical vocabulary is exactly
 * THREE verdicts — Favourable / Remedy Required / Not Permissible. There is NO
 * fourth state: anything not evaluated (unrated / neutral / unrecognised / null /
 * unknown, including `recognised === false`) is treated as the good default and
 * renders as Favourable.
 *
 * PURE module: no React, no writes, no side effects. This governs LABELS + COLOUR
 * SEMANTICS only — it does NOT touch the authoritative confirm path that writes
 * `fixtures[].status` (that stays byte-for-byte in StaffTechnicalPanel).
 *
 * Colours reference the AVBP portal semantic tokens (tokens.css) — green =
 * Favourable (and the unevaluated good default), amber = Remedy Required, red =
 * Not Permissible — with hard hex fallbacks so text can never inherit a light
 * colour and go white-on-white if a var fails to resolve. No invented tokens;
 * AVBP stays green.
 */

/** The canonical disposition vocabulary the engine returns. Legacy engine tag
 *  values (OK/TOL/NO) may still arrive on cached pre-rewire suggestions and are
 *  accepted as a fallback everywhere below. */
export type CanonicalDisposition =
  | 'favourable'
  | 'neutral'
  | 'caution'
  | 'critical'
  | 'unrated';

/** The staff-facing WORD for a disposition — the canonical vocabulary, shared by
 *  both surfaces. Exactly THREE verdicts: Favourable / Remedy Required / Not
 *  Permissible. Anything not evaluated — neutral, unrated, unrecognised, null, an
 *  unknown value, OR `recognised === false` — is the good default and returns
 *  "Favourable"; there is NO separate neutral / unrated / no-ruling verdict. The
 *  `recognised` param is accepted for signature compatibility only. */
export function dispositionLabel(
  disposition: string | null | undefined,
  _recognised: boolean = true,
): string {
  switch ((disposition || '').toLowerCase()) {
    case 'critical':
    case 'no':
      return 'Not Permissible';
    case 'caution':
    case 'tol':
      return 'Remedy Required';
    case 'favourable':
    case 'ok':
    default:
      // favourable/ok AND every unevaluated value (neutral / unrated /
      // unrecognised / null / unknown) → the good default.
      return 'Favourable';
  }
}

/** The CSS chip class for a disposition (workspace `.evalws-dispo-disp` chips).
 *  Keep these strings identical to the CSS in evaluate-workspace.css. */
export function dispositionChipClass(disposition: string | null | undefined): string {
  switch ((disposition || '').toLowerCase()) {
    case 'critical':
    case 'no':
      return 'is-no';
    case 'caution':
    case 'tol':
      return 'is-tol';
    case 'favourable':
    case 'ok':
    default:
      // unevaluated (neutral / unrated / null / unknown) → the green good default.
      return 'is-ok';
  }
}

/** The inline colour (border + badge ink) for a disposition, for the hex-styled
 *  inline panel. Mirrors the workspace chip semantics via AVBP tokens: green =
 *  Favourable (and the unevaluated good default), amber = Remedy Required, red =
 *  Not Permissible. */
export function dispositionColor(disposition: string | null | undefined): string {
  switch ((disposition || '').toLowerCase()) {
    case 'critical':
    case 'no':
      return 'var(--crit-ink, #991B1B)';
    case 'caution':
    case 'tol':
      return 'var(--warn-ink, #92400E)';
    case 'favourable':
    case 'ok':
    default:
      // unevaluated (neutral / unrated / db-miss / null) → the green good default.
      return 'var(--good-ink, #166534)';
  }
}

/** True when the engine returned no authored ruling for this cell (db-miss / an
 *  unrated or absent disposition). Shared so both surfaces treat "no ruling" the
 *  same way (calm state + the admin "add ruling" entry point). */
export function isNoRuling(s: {
  source?: string | null;
  disposition?: string | null;
}): boolean {
  return s.source === 'db-miss' || s.disposition === 'unrated' || s.disposition == null;
}

/** Map an owner colour-NAME (the XLS `corrective_colour`, e.g. "Blue", "Red",
 *  "Green/Brown", "Off White") to a CSS colour for the corrective-colour swatch.
 *  Falls back to a neutral for any name we don't recognise — the label text still
 *  shows. SHARED (Task: AUO-list unification 2026-07-01) so the inline staff panel
 *  (AuoReviewPanel) and the workspace list (EvalDispositionList) render the SAME
 *  swatch for the same ruling. Extracted verbatim from AuoReviewPanel. */
export function swatchCss(name: string | null | undefined): string {
  const key = String(name || '').trim().toLowerCase();
  const MAP: Record<string, string> = {
    red: '#d62828',
    'red/ orange': '#e85d2a',
    'red/orange': '#e85d2a',
    orange: '#f4811f',
    yellow: '#f4c430',
    green: '#2e8b57',
    'green/brown': '#6b8e23',
    'green/ brown': '#6b8e23',
    brown: '#7b4b2a',
    blue: '#1f6fb2',
    'black/blue': '#1a2a4a',
    'black/ blue': '#1a2a4a',
    black: '#222222',
    white: '#f5f3ee',
    'off white': '#efe9dc',
    'off-white': '#efe9dc',
    grey: '#9ca3af',
    gray: '#9ca3af',
    silver: '#c0c0c0',
    'sky blue': '#7bb6e0',
    pink: '#e58fa6',
    purple: '#7c3aed',
    cream: '#f3ead3',
  };
  // try exact, then the first colour token (e.g. "Blue (dark)" → "blue")
  if (MAP[key]) return MAP[key];
  const first = key.split(/[\s/(]/)[0];
  return MAP[first] || '#cbb89a';
}
